import { produce, setAutoFreeze, Draft } from "immer";

export type Reviver<T> = (o: any) => T

// It's good practice to keep auto-freezing on to enforce immutability.
setAutoFreeze(true);

const ISO_DATE_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?Z$/;

const isPlainObject = (val: unknown): val is Record<string, unknown> => {
  if (val === null || typeof val !== "object") return false;
  const proto = Object.getPrototypeOf(val);
  return proto === Object.prototype || proto === null;
};

function maybeDate(s: string): Date | undefined {
  if (!ISO_DATE_REGEX.test(s)) return;
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Recursively walks through a plain object or array and mutates it
 * by converting ISO date strings into Date objects.
 * IMPORTANT: This function is designed to mutate its input.
 */
function reviveDraft(node: any): void {
  if (node == null) return;

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const v = node[i];
      if (typeof v === "string") {
        const dt = maybeDate(v);
        if (dt) node[i] = dt;
      } else if (Array.isArray(v) || isPlainObject(v)) {
        // Recurse into nested structures.
        reviveDraft(v);
      }
    }
    return;
  }

  if (isPlainObject(node)) {
    for (const key of Object.keys(node)) {
      const v = (node as any)[key];
      if (typeof v === "string") {
        const dt = maybeDate(v);
        if (dt) (node as any)[key] = dt;
      } else if (Array.isArray(v) || isPlainObject(v)) {
        // Recurse into nested structures.
        reviveDraft(v);
      }
    }
  }
}

/**
 * Creates a deep, mutable clone of a value and then revives date strings within it.
 * This function is now decoupled from Immer's `produce`.
 */
function revivePlain<T extends Record<string, unknown> | unknown[]>(value: T): T {
  // Create a deep, mutable copy. This is the key to fixing the error.
  // It "thaws" a potentially frozen object passed as input.
  // Note: This method has limitations (loses functions, undefined, etc.),
  // but it's suitable for this specific use case of reviving dates from JSON-like data.
  const mutableCopy = JSON.parse(JSON.stringify(value));

  // Mutate the new copy to revive the dates.
  reviveDraft(mutableCopy);

  return mutableCopy;
}

// This part of your class instantiation logic remains the same, as it's sound.
function defineOwnData(target: object, data: Record<string, unknown>) {
  const descs: Record<string, PropertyDescriptor> = {};
  for (const [k, v] of Object.entries(data)) {
    descs[k] = { value: v, writable: true, enumerable: true, configurable: true };
  }
  Object.defineProperties(target, descs);
}

// Overloads remain the same.
export function revive<T>(input: T): T;
export function revive<T>(ctor: new (...args: any[]) => T, data: object): T;
export function revive<T>(arg1: any, arg2?: any): T {
  // Logic for instantiating a class.
  if (typeof arg1 === "function") {
    const ctor = arg1 as new (...args: any[]) => T;

    const rawData =
      arg2 && (isPlainObject(arg2) || Array.isArray(arg2)) ? arg2 : {};

    // The `revivePlain` function now safely handles potentially frozen data.
    const revivedData = revivePlain(rawData as any) as Record<string, unknown>;

    const inst = Object.create(null);
    defineOwnData(inst, revivedData);
    Object.setPrototypeOf(inst, ctor.prototype);
    return inst as T;
  }

  // Logic for reviving a plain value.
  const v = arg1 as T;
  if (typeof v === "string") {
    return (maybeDate(v) ?? v) as T;
  }

  if (Array.isArray(v) || isPlainObject(v)) {
    // The `revivePlain` function now safely handles potentially frozen data.
    return revivePlain(v as any) as T;
  }

  // For class instances, functions, or primitives: return as-is.
  return v;
}

function getReservedKeys(ctor: new (...args: any[]) => any): Set<string> {
  const reserved = new Set<string>()

  // Walk the prototype chain if you have inheritance (optional)
  for (let p = ctor.prototype; p && p !== Object.prototype; p = Object.getPrototypeOf(p)) {
    const descs = Object.getOwnPropertyDescriptors(p)
    for (const [key, d] of Object.entries(descs)) {
      if (key === 'constructor') continue
      // Treat methods and accessors as "reserved"
      if (typeof d.value === 'function' || typeof d.get === 'function' || typeof d.set === 'function') {
        reserved.add(key)
      }
    }
  }
  return reserved
}

// Generic revive (does NOT run constructor)
export function reviveWithProto<T>(ctor: new (...args: any[]) => T, data: any, seed?: (inst: any) => void): T {
  const inst: any = Object.create(ctor.prototype)

  // Seed invariants the class expects (before any getter can run)
  seed?.(inst)

  const reserved = getReservedKeys(ctor)

  // Copy only data props; skip anything that would shadow methods/getters/setters
  for (const [k, v] of Object.entries(data ?? {})) {
    if (reserved.has(k)) continue
    inst[k] = revive(v)
  }
  return inst as T
}

// Convert an object to plain JSON while preserving enumerable properties not
// returned by a custom `toJSON` implementation. Private fields (prefixed with
// `_`) and functions are skipped.
export function toJSONWithExtras(value: any): any {
  if (value == null) return value
  if (Array.isArray(value)) return value.map(toJSONWithExtras)
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object') {
    const base =
      typeof (value as any).toJSON === 'function'
        ? (value as any).toJSON()
        : {}
    const result: any = { ...base }
    for (const [k, v] of Object.entries(value)) {
      // if (k.startsWith('_')) continue
      if (typeof v === 'function') continue
      if (k in result && result[k] !== undefined) continue
      result[k] = toJSONWithExtras(v)
    }
    return result
  }
  return value
}

